"use client"

import { useEffect, useState } from "react"

/** Smallest a tile may get, in px, before Auto adds no more columns. */
export const AUTO_MIN_TILE_PX = 200
export const AUTO_MIN_COLUMNS = 2
export const AUTO_MAX_COLUMNS = 8
// The grid has p-4 (16px) on each side.
const GRID_SIDE_PADDING_PX = 32

/** How many columns fit a grid `width` px wide, with `gap` px between tiles: as many as keep every tile at least AUTO_MIN_TILE_PX wide,
 * never fewer than 2 (a phone still shows two) or more than 8. */
export function autoColumnCount(width: number, gap: number): number {
  const usable = Math.max(0, width - GRID_SIDE_PADDING_PX)
  const fit = Math.floor((usable + gap) / (AUTO_MIN_TILE_PX + gap))
  return Math.min(AUTO_MAX_COLUMNS, Math.max(AUTO_MIN_COLUMNS, fit))
}

/** The Auto column count for the element the grid scrolls in (or the window), updated as it is resized. */
export function useAutoGridColumns(container: React.RefObject<HTMLElement | null> | undefined, gap: number, enabled: boolean): number {
  const [width, setWidth] = useState(() => (typeof window === "undefined" ? 1200 : window.innerWidth))

  useEffect(() => {
    if (!enabled) return
    const element = container?.current ?? null
    let frame = 0
    const measure = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => setWidth(element?.clientWidth || window.innerWidth))
    }
    measure()
    window.addEventListener("resize", measure)
    const observer = element && typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null
    if (element) observer?.observe(element)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener("resize", measure)
      observer?.disconnect()
    }
  }, [container, enabled])

  return autoColumnCount(width, gap)
}
